/* eslint-disable camelcase */
import { useMutation } from '@apollo/client'
import { useAuth0 } from '@auth0/auth0-react'
import React, { useContext } from 'react'

import {
  SetHoldUntilMondayOnCartDocument,
  SetShippingMethodsOnCartDocument,
} from '../../../graphql/magento'
import { TokenContext } from '../../../layouts/context'
import { NonEmptyCartContext } from '../../../lib/cart/context'
import useLegalAgreementsGuard from '../../../lib/cart/hooks/useLegalAgreementsGuard'
import usePatientOnboardingCompleteGuard from '../../../lib/cart/hooks/usePatientOnboardingCompleteGuard'
import useRegistrationGuard from '../../../lib/cart/hooks/useRegistrationGuard'
import { logAndCaptureMessage } from '../../../utils/errorTools'
import { userClaimIds } from '../../../utils/userClaims'
import Spinner from '../../Spinner'
import Delivery from './Delivery'

const DeliveryContainer = () => {
  const { loadingRedirect, shouldRedirect } = useRegistrationGuard()
  usePatientOnboardingCompleteGuard()
  useLegalAgreementsGuard()
  const token = useContext(TokenContext)
  const customerCart = useContext(NonEmptyCartContext)
  const shippingAddress = customerCart.shipping_addresses[0]
  const shippingMethods = shippingAddress.available_shipping_methods
    .filter(({ available }) => !!available)
    .sort((a, b) => (a.amount.value > b.amount.value ? 1 : -1))
  const [setShippingMethodsOnCart, { loading: loadingSetShippingMethod }] = useMutation(
    SetShippingMethodsOnCartDocument,
    { context: { token } },
  )
  const [setHoldUntilMondayOnCart, { loading: loadingSetHoldUntilMonday }] = useMutation(
    SetHoldUntilMondayOnCartDocument,
    { context: { token } },
  )

  const { user } = useAuth0()
  if (shippingMethods.length === 0) {
    logAndCaptureMessage(
      `No shipping methods available for cart with details: ${JSON.stringify({
        netSuiteId: user?.[userClaimIds.netSuiteId],
        auth0UserId: user?.sub,
        cartSubTotal: customerCart.prices?.subtotal_with_discount_excluding_tax,
        shippingAddress,
      })}`,
    )
  }

  const handleDeliveryMethodChange = (method) => {
    setShippingMethodsOnCart({
      variables: {
        cartId: customerCart.id,
        shippingMethods: [
          {
            carrier_code: method.carrier_code,
            method_code: method.method_code,
          },
        ],
      },
    })
  }

  const handleHoldUntilMondayChange = (holdUntilMonday) => {
    setHoldUntilMondayOnCart({
      variables: {
        cartId: customerCart.id,
        holdUntilMonday,
      },
    })
  }

  if (loadingRedirect || shouldRedirect) {
    return <Spinner loading />
  }

  return (
    <Delivery
      handleHoldUntilMondayChange={handleHoldUntilMondayChange}
      defaultHoldUntilMonday={customerCart.hold_until_monday === '1'}
      handleDeliveryMethodChange={handleDeliveryMethodChange}
      submitting={loadingSetShippingMethod || loadingSetHoldUntilMonday}
      methods={shippingMethods}
      defaultMethod={shippingAddress.selected_shipping_method}
      address={shippingAddress}
      items={customerCart.items}
    />
  )
}

export default DeliveryContainer
